import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';

import { HeaderAlignmentService } from '@finder/shared/services/header-alignment/header-alignment.service';
import { ScrollToService } from '@finder/shared/services/scroll-to/scroll-to.service';
import { IPicture } from '../picture/picture.interface';
import { DisclaimerComponent } from '../disclaimer/disclaimer.component';
import { EmailCaptureComponent } from '../email-capture/email-capture.component';
import { ImageDrawerComponent } from '../image-drawer/image-drawer.component';
import { LottieBgComponent } from '../lottie-bg/lottie-bg.component';
import { LottieComponent } from '../lottie/lottie.component';
import { MultiCtaComponent } from '../multi-cta/multi-cta.component';
import { PageMessageComponent } from '../page-message/page-message.component';
import { PictureComponent } from '../picture/picture.component';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { ScrollimationComponent } from '../scrollimation/scrollimation.component';
import { TextLogoComponent } from '../text-logo/text-logo.component';
import { InViewportDirective } from '@finder/shared/directives/in-viewport/in-viewport.directive';
import { AriaLabelHtmlDirective } from '@finder/shared/directives/aria-label-html/aria-label-html.directive';

@Component({
    selector: 'finder-content-header',
    templateUrl: './content-header.component.html',
    styleUrls: ['./content-header.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        DisclaimerComponent,
        EmailCaptureComponent,
        ImageDrawerComponent,
        LottieBgComponent,
        LottieComponent,
        MultiCtaComponent,
        PageMessageComponent,
        PictureComponent,
        SafeHtmlPipe,
        ScrollimationComponent,
        TextLogoComponent,
        InViewportDirective, // finderInViewport,
        AriaLabelHtmlDirective, //finderAriaLabelHtml
    ]
})
export class ContentHeaderComponent implements OnInit {
    @Input() headerData: any;
    @Input() isDetailPage = false;
    @Output() triggerPlayback = new EventEmitter<boolean>();

    @ViewChild('headerText', { static: true }) headerText: ElementRef;

    // We will set the default to assume there is no CTA in the header
    noCta = true;
    needsMargin = false;
    hasBgItems = false;
    hasSponsor = false;
    hasTextReveal = false;
    hideText = false;
    hasAnimationReveal = false;
    hideAnimation = false;
    sponsorImage: IPicture;

    private scrollLock: boolean;
    private scrollToElement: string;

    constructor(
        private headerAlignmentServce: HeaderAlignmentService,
        private el: ElementRef,
        private scrollToService: ScrollToService,
        @Inject(DOCUMENT) private document: Document,
    ) { }

    ngOnInit() {
        const hasCta = this.headerData.ctaEnabled === true || this.headerData.ctaEmailEnabled === true;
        this.noCta = !hasCta;
        this.headerData = Object.assign(this.headerData, this.headerAlignmentServce.getHeaderAlignment(this.headerData));

        // If we have no subtitle _and_ a CTA, the bottom margin for the title is a bit tight,
        // so let's make it larger
        if (!this.headerData.subtitle && this.headerData.ctaEnabled === true) {
            this.needsMargin = true;
        }

        this.setLottie();
        this.setScrollimation();
        this.setBgMedia();
        this.setSponsorMedia();
    }

    headerVisible() {
        this.triggerPlayback.emit(true);
    }

    // For scrubbing we can trigger an animation reveal when we enter the component
    scrollEnter(e) {
        // If there is no animation reveal we don't need to bother
        if (!this.hasAnimationReveal) {
            return;
        }

        // Set animation display - this allows us to toggle when scrubbing up/down
        this.hideAnimation = e === false;
    }

    // For scrubbing we can trigger a title text reveal. We can also enable scroll locking so
    // the guest is forced to watch the animation, and with that we can enable the ability to
    // then scroll to an element _after_ the animation is complete - sort of a way to let the
    // guest know "hey, you can scroll again!"

    // There is also some cursor styling that happens on scroll lock, but just in case...
    scrollLeave(e) {
        // If we are revealing or scrolling to text we don't need to bother
        if (!this.hasTextReveal && !this.scrollToElement) {
            return;
        }

        // We only want to bother with the scroll to when locking is enabled
        if (this.scrollLock && this.scrollToElement) {
            // Get element to scroll to
            const scrollEl = this.document.querySelector(this.scrollToElement);
            if (scrollEl) {
                // Smooth scroll to element
                // TODO: Add scroll delay
                this.scrollToService.scrollToElement(scrollEl, true);
            }

            // If we are not _also_ doing a text reveal we are done
            if (!this.hasTextReveal) {
                return;
            }
        }

        // Set text display - this allows us to toggle when scrubbing up/down
        this.hideText = e === false;
    }

    private setLottie() {
        // Lottie settings - we only need to proceed if we actaully have some bg items
        const bgItems = this.headerData?.bgItems ?? [];
        if (!bgItems.length) {
            return;
        }

        // Set the prop so we can attach some styling
        this.hasBgItems = true;
        this.setMargins();

        this.el.nativeElement.style.setProperty('--finder-lottie-player-display', 'block');
        this.el.nativeElement.style.setProperty('--finder-lottie-player-justify-content', 'initial');
        this.el.nativeElement.style.setProperty('--finder-lottie-player-position', 'absolute');
        this.el.nativeElement.style.setProperty('--finder-lottie-player-overflow', 'visible');
    }

    private setScrollimation() {
        const scrollData = this.headerData?.titleScroll;
        if (!scrollData) {
            return;
        }

        this.el.nativeElement.style.setProperty('--ct-header-header-text-height', scrollData.height);
        this.el.nativeElement.style.setProperty('--ct-header-header-text-display', scrollData.display || 'block');
        this.el.nativeElement.style.setProperty('--ct-header-header-text-flex-direction', scrollData.flexDirection || 'unset');
        this.el.nativeElement.style.setProperty('--ct-header-header-text-justify-content', scrollData.justifyContent || 'unset');
        this.el.nativeElement.style.setProperty('--ct-header-header-text-padding-bottom', scrollData.paddingBottom || 'unset');

        this.hideText = this.hasTextReveal = scrollData.textReveal === true;
        this.hideAnimation = this.hasAnimationReveal = scrollData.animationReveal === true;
        this.scrollLock = scrollData.enableScrollLock ?? false;
        this.scrollToElement = scrollData.scrollToElement;
    }

    // We should stress to producers that they should probably control the margins. The infer logic
    // below is pretty solid but without the producer explicitly defining the margin we could get the
    // logic wrong.
    private setMargins() {
        // Let's get some details from the bgItems so we can determine what the margins should be.
        const marginLeft = this.getMarginValue('Left');
        const marginRight = this.getMarginValue('Right');

        if (marginLeft) {
            this.headerText.nativeElement.style.setProperty('margin-left', marginLeft);
        }

        if (marginRight) {
            this.headerText.nativeElement.style.setProperty('margin-right', marginRight);
        }
    }

    private getMarginValue(marginPlacement) {
        // Ideally we will have specific margins in the config
        const marginKey = `margin${marginPlacement}`;
        const marginObject = this.headerData.bgItems.find(item => item[marginKey]);
        if (marginObject) {
            return marginObject[marginKey];
        }

        // If we don't have specific margins we'll start using some (suspect) logic to try and
        // determine the margin
        return this.inferMarginValue(marginPlacement);
    }

    private inferMarginValue(marginPlacement) {
        let marginValue;

        const marginKey = marginPlacement.toLowerCase();
        const marginObject = this.headerData.bgItems.find(item => item[marginKey]);
        if (marginObject) {
            // If the object places the bg from the placement side, we will take the width of the
            // bg lottie and set the margin for the placement side.

            // Example: if we want to check for 'left' placement, we will look for a bg item that
            // sets the 'left' property - that means we _assume_ that since it sets the left offset
            // that it must be the bg image on the left, so using the width as the left margin would
            // theoretically work.
            marginValue = marginObject.width;
            if (marginValue && marginValue.endsWith('px')) {
                // Add a buffer
                marginValue = `${
                    Number(marginValue.replace('px', '')) + // Width
                    Number(marginObject[marginKey].replace('px', '')) + // Offset
                    10 // Buffer
                }px`;
            }
        }

        return marginValue;
    }

    private setBgMedia() {
        const bgMedia = this.headerData.backgroundMedia;
        if (!bgMedia || bgMedia === 'false') {
            return;
        }

        this.document.body.style.setProperty('--finder-theme-campaign-top-section-mobile-bg-image', `url(${bgMedia.mobile})`);
        this.document.body.style.setProperty('--finder-theme-campaign-top-section-bg-image', `url(${bgMedia.desktop})`);
    }

    private setSponsorMedia() {
        if (!this.headerData.sponsorMedia) {
            return;
        }

        this.hasSponsor = true;

        // Create object for picture component
        this.sponsorImage = {
            desktop: this.headerData.sponsorMedia.desktop,
            mobile: this.headerData.sponsorMedia.mobile,
            alt: this.headerData.sponsorMedia.alt
        };
    }
}
